import {LanguageSelectorTranslationProvider} from 'microsites/core/chatMicrosite/components/languageSelector/useLanguageSelectorTranslations';
import {defaultMicrositeFeatureFlags} from 'microsites/core/chatMicrosite/runtime/featureFlags';
import {MicrositeRouteRegistryProvider} from 'microsites/core/chatMicrosite/runtime/MicrositeRouteRegistryContext';
import {MicrositeRuntimeProvider} from 'microsites/core/chatMicrosite/runtime/MicrositeRuntimeProvider';
import {brand} from '../brand';
import {useBrandLogoImage} from './assets/images/useBrandLogoImage';
import {useFauxInstagramImage, useMicrositeImage} from './assets/images/useMicrositeImage';
import {CHATCOFFEE_LANGUAGE_ORDER} from './components/languageManifest';
import {chatCoffeeRouteDefinitions} from './routes/routeDefinitions';
import type {ImageKey} from './assets/images/images';
import type {LanguageSelectorTranslation} from './components/languageSelector/languageSelectorTranslationType';
import type {LanguageId} from 'microsites/core/chatMicrosite/translations/utils/languageManifest';
import type {ReactNode} from 'react';

type LanguageSelectorTranslationCarrier = {
  readonly languageSelectorTranslation: LanguageSelectorTranslation;
  readonly languageSelectorEnglishTranslation: LanguageSelectorTranslation;
};

const useMicrositeImageForRuntime = (imageId: string) => useMicrositeImage(imageId as ImageKey);
const useFauxInstagramImageForRuntime = (imageId: string) => useFauxInstagramImage(imageId as ImageKey);

const TranslationProviders = <TTranslations extends LanguageSelectorTranslationCarrier>({children, translations}: {readonly children: ReactNode; readonly translations: TTranslations}) => {
  const {languageSelectorTranslation, languageSelectorEnglishTranslation} = translations;
  return (
    <LanguageSelectorTranslationProvider
      value={{
        locale: languageSelectorTranslation,
        english: languageSelectorEnglishTranslation,
        languageOrder: CHATCOFFEE_LANGUAGE_ORDER,
      }}
    >
      {children}
    </LanguageSelectorTranslationProvider>
  );
};

type MicrositeProviderProps<TPageProps extends LanguageSelectorTranslationCarrier> = {
  readonly locale: LanguageId;
  readonly children: ReactNode;
  readonly pageProps: TPageProps;
};

export const ChatCoffeeMicrositeProvider = <TPageProps extends LanguageSelectorTranslationCarrier>({locale, children, pageProps}: MicrositeProviderProps<TPageProps>) => (
  <MicrositeRuntimeProvider
    brandId={brand.id}
    locale={locale}
    featureFlags={defaultMicrositeFeatureFlags}
    imageHooks={{
      useMicrositeImage: useMicrositeImageForRuntime,
      useFauxInstagramImage: useFauxInstagramImageForRuntime,
      useBrandLogoImage,
    }}
  >
    <MicrositeRouteRegistryProvider routes={chatCoffeeRouteDefinitions}>
      <TranslationProviders translations={pageProps}>{children}</TranslationProviders>
    </MicrositeRouteRegistryProvider>
  </MicrositeRuntimeProvider>
);
